import type { ComponentProps, ReactNode } from "react";
import { useAddressAutocomplete, type AddressSuggestion } from "../hooks/useAddressAutocomplete";
import { AddressSuggestionListbox } from "./AddressSuggestionListbox";
import { Input } from "./Input";

// `Omit<…, "onSelect">` replaces the native DOM `onSelect` (text-selection)
// handler with our suggestion-selection callback.
export interface AddressAutocompleteInputProps extends Omit<
  ComponentProps<typeof Input>,
  "onSelect"
> {
  /**
   * Smarty embedded key, passed by the calling SPA because shared components
   * never read `import.meta.env`. Without a key it renders a plain `Input`:
   * no client, no fetches, no dropdown.
   */
  embeddedKey?: string | undefined;
  onSelect: (suggestion: AddressSuggestion) => void;
  /** User-visible dropdown strings. They default to English; the enrollment form passes translations. */
  loadingText?: string | undefined;
  noMatchesText?: string | undefined;
  suggestionsLabel?: string | undefined;
}

/**
 * Street-address `Input` with a Smarty US Autocomplete Pro dropdown.
 *
 * It follows the WAI-ARIA combobox pattern and works both uncontrolled under a
 * react-hook-form `{...register(...)}` spread and controlled via
 * `value`/`onChange`. Selecting a suggestion calls `onSelect` with the mapped
 * fields; the caller writes them.
 */
export function AddressAutocompleteInput({
  embeddedKey = "",
  onSelect,
  loadingText,
  noMatchesText,
  suggestionsLabel,
  onChange,
  onBlur,
  onKeyDown,
  disabled,
  ...inputProps
}: AddressAutocompleteInputProps): ReactNode {
  const ac = useAddressAutocomplete({ embeddedKey, onSelect, disabled });

  if (!ac.enabled) {
    return (
      <Input
        {...inputProps}
        disabled={disabled}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
      />
    );
  }

  return (
    <div className="relative">
      <Input
        {...inputProps}
        {...ac.comboboxProps}
        disabled={disabled}
        onChange={(e) => {
          onChange?.(e);
          ac.handleInputChange(e.target.value);
        }}
        onBlur={(e) => {
          onBlur?.(e);
          ac.handleBlur();
        }}
        onKeyDown={(e) => {
          onKeyDown?.(e);
          ac.handleKeyDown(e);
        }}
      />
      <AddressSuggestionListbox
        ac={ac}
        loadingText={loadingText}
        noMatchesText={noMatchesText}
        suggestionsLabel={suggestionsLabel}
      />
    </div>
  );
}
